import { useState, useEffect, useCallback } from 'react'
import { listingAPI } from '../services/api/listingAPI'
import { createLogger } from '../utils/logger'

const logger = createLogger('useNearbyListings')

/**
 * useNearbyListings - fetches active listings within radius of a lat/lng point.
 * Re-fetches when lat/lng/radius/category changes.
 * @param {number} lat
 * @param {number} lng
 * @param {number} [radius=5] - km
 * @param {string} [category]  - optional category filter
 * Returns { listings, loading, error, refresh }
 */
export function useNearbyListings(lat, lng, radius = 5, category = '') {
  const [listings, setListings] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [bust, setBust] = useState(0) // incremented to force re-fetch past HTTP cache

  const load = useCallback(() => {
    if (!lat || !lng) return
    let cancelled = false
    setLoading(true)
    setError(null)
    listingAPI
      .getNearbyListings(lat, lng, radius, category)
      .then((res) => {
        if (!cancelled) setListings(res?.data?.listings || [])
      })
      .catch((err) => {
        if (!cancelled) {
          logger.error('Failed to load nearby listings', err)
          setError(err)
        }
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [lat, lng, radius, category, bust]) // bust in deps so refresh() forces a new fetch

  useEffect(() => load(), [load])

  const refresh = useCallback(() => setBust(b => b + 1), [])

  return { listings, loading, error, refresh }
}
